import React from "react";
import { Card, CardContent, Typography, Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatAppName } from "../utils/connectorUtils";

interface AvailableIntegrationCardProps {
  logo: string;
  name: string;
  description: string;
  badge?: string;
  connected?: boolean;
  connectLabel?: string;
  disabled?: boolean;
  onConnect: () => void;
}

const AvailableIntegrationCard: React.FC<AvailableIntegrationCardProps> = ({
  logo,
  name,
  description,
  badge,
  connected = false,
  connectLabel = "Connect",
  disabled = false,
  onConnect,
}) => {
  return (
    <Card
      sx={{
        borderRadius: "8px",
        boxShadow: "0px 2px 8px rgba(16, 30, 54, 0.04)",
        minWidth: 260,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "24px 24px 0px 24px",
        position: "relative",
        border: connected ? "1px solid #C9E4C9" : "1px dashed #C5C5C5",
        background: connected ? "#FBFDFB" : "#fff",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: 12,
            background: "#F5F5F5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={logo}
            alt={name}
            style={{ width: 48, height: 48, objectFit: "contain" }}
          />
        </Box>
        {badge ? (
          <Box
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              color: "#705D4B",
              background: "#F4EFE7",
              borderRadius: "999px",
              px: 1.25,
              py: 0.5,
            }}
          >
            {badge}
          </Box>
        ) : null}
      </Box>
      <CardContent sx={{ p: 0.5, pt: 1, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {formatAppName(name)}
          </Typography>
          {connected ? (
            <CheckCircleIcon sx={{ fontSize: 18, color: "#2E7D32" }} aria-label="Connected" />
          ) : null}
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: 13,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {description}
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", mt: "auto" }}>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={onConnect}
            sx={{
              px: 0,
              fontSize: 16,
              fontWeight: 700,
              fontFamily: "var(--theo-font-sans)",
              color: "#705D4B",
              textTransform: "none",
              alignSelf: "flex-start",
              justifyContent: "flex-start",
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            {connected ? "Add another" : connectLabel}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AvailableIntegrationCard;
